/**
 * jf-model.ts — the JOURNEY flow model: one model for queue, appointment and
 * event journeys. Promoted out of screens/queues on 2026-08-25 (operator: the
 * Queues section is being decommissioned; the modeler is not) — screens/queues
 * keeps a re-export shim so the queue flows page compiles untouched until it
 * goes.
 *
 * Started life as the SIBLING of the messaging flow builder's fb-model, adapted
 * to the queue lifecycle (operator decision 2026-07-17: same visual language,
 * queue-specific model). What used to be queue-specific now lives in a QFVocab
 * descriptor, so the same canvas serves all three interaction types.
 *
 * Structural reuse: step shapes are layout-compatible with fb-model's FBStep
 * (same `kind` discriminants for comms/wait/decision/survey/exit + tree helpers
 * with identical semantics), so the queue builder reuses fb-layout's pure
 * auto-layout engine unchanged. The queue-specific additions are:
 *   • "stage" steps — the queue lifecycle states (waiting / called / serving /
 *     done) that track wait time and service time,
 *   • queue triggers, queue decision conditions, queue exit outcomes
 *     (Served / Left queue / No-show — the Unserved states).
 */

export const QF_NODE_W = 272;
import { t as i18nT } from "../../../lib/i18n";
import { qpDefaultCfg, type QPageId } from "../../queues/qp-pages";
import { fpDefaultPaths, fpMigrate, fpMapSteps, FP_COMMON_LABELS, type FlowPath } from "../../builder/flow-paths";
export { fpLabel, fpStyle, fpAddPath, fpRemovePath, FP_MAX_PATHS, FP_CUSTOM, FP_COMMON_LABELS, type FlowPath } from "../../builder/flow-paths";

export interface QFTrigger { id: string; label: string; labelKey: string; sub: string; }
// Triggers reference the journey's SCREENS (PM direction 2026-07-17) — the old
// lifecycle-event triggers (joined/transferred/rejoined) are migrated below.
export const QF_TRIGGERS: QFTrigger[] = [
  { id: "screen_services", label: "Services screen is opened", labelKey: "queues.flow.trigger.screen_services.label",       get sub() { return i18nT("queues.flow.trigger.screen_services.sub", "QR code, kiosk or link — the journey starts on the Services screen"); } },
  { id: "screen_wait",     label: "Wait Time screen is opened", labelKey: "queues.flow.trigger.screen_wait.label",      get sub() { return i18nT("queues.flow.trigger.screen_wait.sub", "The service is pre-picked (single-service queue or staff link) — start at the wait estimate"); } },
  { id: "screen_contact",  label: "Contact Details screen is opened", labelKey: "queues.flow.trigger.screen_contact.label", get sub() { return i18nT("queues.flow.trigger.screen_contact.sub", "Service and wait already known — start at contact capture"); } },
  { id: "screen_waiting",  label: "Waiting in queue screen is opened", labelKey: "queues.flow.trigger.screen_waiting.label", get sub() { return i18nT("queues.flow.trigger.screen_waiting.sub", "Staff added the customer — the journey starts already in the queue"); } },
];
const QF_TRIGGER_MIGRATE: Record<string, string> = { joined: "screen_services", transferred: "screen_waiting", rejoined: "screen_services" };
export const QF_TRIGGER = (id: string) => QF_TRIGGERS.find((t) => t.id === (QF_TRIGGER_MIGRATE[id] ?? id)) ?? QF_TRIGGERS[0];

/** In-flow trigger steps (operator 2026-07-21): lifecycle moments placed ON the
 *  canvas — the steps after a trigger run when that moment happens. */
export const QF_FLOW_TRIGGERS: { id: string; label: string; labelKey: string; sub: string }[] = [
  { id: "joined",  label: "Joined the Queue", labelKey: "queues.flow.flowTrigger.joined.label",      get sub() { return i18nT("queues.flow.flowTrigger.joined.sub", "Fires the moment the customer joins"); } },
  { id: "update",  label: "Queue Update", labelKey: "queues.flow.flowTrigger.update.label",          get sub() { return i18nT("queues.flow.flowTrigger.update.sub", "Fires when their position or wait changes"); } },
  { id: "left",    label: "Left without Service", labelKey: "queues.flow.flowTrigger.left.label",  get sub() { return i18nT("queues.flow.flowTrigger.left.sub", "Fires when they leave unserved (left / no-show)"); } },
  { id: "served",  label: "Service Delivery", labelKey: "queues.flow.flowTrigger.served.label",     get sub() { return i18nT("queues.flow.flowTrigger.served.sub", "Fires when the service is delivered"); } },
];
export const QF_FLOW_TRIGGER = (id: string) => QF_FLOW_TRIGGERS.find((t) => t.id === id) ?? QF_FLOW_TRIGGERS[0];

/** Queue lifecycle stages — first-class nodes on the canvas. */
export type QFStageId = "waiting" | "called" | "serving" | "done";
export const QF_STAGES: Record<QFStageId, { label: string; labelKey: string; sub: string; color: string; tint: string }> = {
  waiting: { label: "Waiting", labelKey: "queues.flow.stage.waiting.label",           get sub() { return i18nT("queues.flow.stage.waiting.sub", "Wait time is tracked from here"); },    color: "#C8861E", tint: "#FAF1DE" },
  called:  { label: "It's their turn", labelKey: "queues.flow.stage.called.label",   get sub() { return i18nT("queues.flow.stage.called.sub", "Customer is called up"); },             color: "#2D60EB", tint: "#E8EEFD" },
  serving: { label: "Being served", labelKey: "queues.flow.stage.serving.label",      get sub() { return i18nT("queues.flow.stage.serving.sub", "Service time is tracked from here"); }, color: "#1F8A5B", tint: "#E4F5EF" },
  done:    { label: "Service completed", labelKey: "queues.flow.stage.done.label", get sub() { return i18nT("queues.flow.stage.done.sub", "Checked out — by staff or auto"); },    color: "#475569", tint: "#E7ECF2" },
};
export const QF_STAGE_ORDER: QFStageId[] = ["waiting", "called", "serving", "done"];

export const QF_DECISION_CONDS = [
  "Customer checks in", // ui-config: stored flow value; qfValueText shows it
  "Waited more than 10 minutes", // ui-config: stored flow value; qfValueText shows it
  "Waited more than 20 minutes", // ui-config: stored flow value; qfValueText shows it
  "Replied to the last message", // ui-config: stored flow value; qfValueText shows it
  "No answer after 2 calls", // ui-config: stored flow value; qfValueText shows it
  "Party size is over 4", // ui-config: stored flow value; qfValueText shows it
  "Answered the booking questions", // ui-config: stored flow value; qfValueText shows it
  "Is a repeat visitor", // ui-config: stored flow value; qfValueText shows it
];
export const QF_EXIT_OUTCOMES = ["Served", "Left the queue", "No-show", "Transferred out", "Removed by staff"]; // ui-config: stored flow values; qfValueText shows them
export const QF_WAIT_UNITS = ["Minutes", "Hours", "Days"]; // ui-config: stored flow values; qfValueText shows them
export const QF_WAIT_TIMINGS = [
  { value: "after_prev",    label: "After the previous step", labelKey: "queues.flow.waitTiming.after_prev.label" },
  { value: "after_called",  label: "After the customer is called", labelKey: "queues.flow.waitTiming.after_called.label" },
  { value: "after_service", label: "After the service ends", labelKey: "queues.flow.waitTiming.after_service.label" },
];
export const QF_SENDWHEN = ["Immediately", "After previous step", "When the stage is reached"]; // ui-config: stored flow values; qfValueText shows them

/* ── Per-type vocabulary (2026-08-25) ───────────────────────────────
 * The queue flow builder is becoming the ONE journey modeler: the canvas,
 * the layout, the node vocabulary and the inspector are identical for a
 * queue, an appointment and an event journey — only the WORDS differ. The
 * arrays above stay the queue's set and remain exported unchanged, so every
 * existing caller keeps compiling; QF_VOCABS.queue simply points at them.
 *
 * A descriptor carries the four things that vary, plus the send-when list:
 * the lifecycle moments that can be placed on the canvas, the conditions a
 * decision can test, the outcomes a flow can end on, and what a wait is
 * measured from. Everything else — step kinds, tree helpers, layout, node
 * chrome — is shared.
 */
export type QFVocabId = "queue" | "appointment" | "event";
export interface QFWaitTiming { value: string; label: string; labelKey: string; }
export interface QFVocab {
  id: QFVocabId;
  /** Shown on the canvas toolbar, e.g. "Appointment journey". */
  label: string;
  /** Lifecycle moments placeable as trigger nodes. */
  flowTriggers: { id: string; label: string; labelKey: string; sub: string }[];
  decisionConds: string[];
  exitOutcomes: string[];
  waitTimings: QFWaitTiming[];
  sendWhen: string[];
  /** Labels offered when naming a decision's paths. Same pattern as the
   *  vocabulary above: this type's own wording first, the common ones after. */
  pathLabels: string[];
}

export const QF_VOCAB_QUEUE: QFVocab = {
  id: "queue",
  get label() { return i18nT("journeys.flow.vocab.queue.label", "Queue journey"); },
  flowTriggers: QF_FLOW_TRIGGERS,
  decisionConds: QF_DECISION_CONDS,
  exitOutcomes: QF_EXIT_OUTCOMES,
  waitTimings: QF_WAIT_TIMINGS,
  sendWhen: QF_SENDWHEN,
  pathLabels: ["Checked in", "Didn't check in", "Still waiting", "Left the queue", ...FP_COMMON_LABELS], // ui-config: stored flow values; qfValueText shows them
};

export const QF_VOCAB_APPOINTMENT: QFVocab = {
  id: "appointment",
  get label() { return i18nT("journeys.flow.vocab.appointment.label", "Appointment journey"); },
  flowTriggers: [
    { id: "booked",    label: "Appointment Booked", labelKey: "journeys.flow.flowTrigger.booked.label",       get sub() { return i18nT("journeys.flow.flowTrigger.booked.sub", "Fires the moment the booking is made"); } },
    { id: "changed",   label: "Booking Changed", labelKey: "journeys.flow.flowTrigger.changed.label",         get sub() { return i18nT("journeys.flow.flowTrigger.changed.sub", "Fires when the time, staff or service changes"); } },
    { id: "cancelled", label: "Cancelled or No-show", labelKey: "journeys.flow.flowTrigger.cancelled.label", get sub() { return i18nT("journeys.flow.flowTrigger.cancelled.sub", "Fires when they cancel or fail to arrive"); } },
    { id: "attended",  label: "Appointment Attended", labelKey: "journeys.flow.flowTrigger.attended.label",  get sub() { return i18nT("journeys.flow.flowTrigger.attended.sub", "Fires when the appointment is delivered"); } },
  ],
  decisionConds: [
    "Confirmed the appointment", // ui-config: stored flow value; qfValueText shows it
    "Opened the reminder", // ui-config: stored flow value; qfValueText shows it
    "Arrived on time", // ui-config: stored flow value; qfValueText shows it
    "Paid the deposit", // ui-config: stored flow value; qfValueText shows it
    "Is a first-time customer", // ui-config: stored flow value; qfValueText shows it
    "Rescheduled at least once", // ui-config: stored flow value; qfValueText shows it
  ],
  exitOutcomes: ["Attended", "Cancelled", "No-show", "Rescheduled out"], // ui-config: stored flow values; qfValueText shows them
  pathLabels: ["Confirmed", "Not confirmed", "Rescheduled", "Cancelled", ...FP_COMMON_LABELS], // ui-config: stored flow values; qfValueText shows them
  waitTimings: [
    { value: "after_prev",   label: "After the previous step", labelKey: "journeys.flow.waitTiming.after_prev.label" },
    { value: "before_appt",  label: "Before the appointment", labelKey: "journeys.flow.waitTiming.before_appt.label" },
    { value: "after_appt",   label: "After the appointment", labelKey: "journeys.flow.waitTiming.after_appt.label" },
  ],
  sendWhen: ["Immediately", "After previous step", "When the page is reached"], // ui-config: stored flow values; qfValueText shows them
};

export const QF_VOCAB_EVENT: QFVocab = {
  id: "event",
  get label() { return i18nT("journeys.flow.vocab.event.label", "Event journey"); },
  flowTriggers: [
    { id: "registered", label: "Registered", labelKey: "journeys.flow.flowTrigger.registered.label",        get sub() { return i18nT("journeys.flow.flowTrigger.registered.sub", "Fires the moment they register or RSVP yes"); } },
    { id: "ticketed",   label: "Tickets Issued", labelKey: "journeys.flow.flowTrigger.ticketed.label",      get sub() { return i18nT("journeys.flow.flowTrigger.ticketed.sub", "Fires when tickets are issued or paid for"); } },
    { id: "declined",   label: "Declined or Cancelled", labelKey: "journeys.flow.flowTrigger.declined.label", get sub() { return i18nT("journeys.flow.flowTrigger.declined.sub", "Fires when they decline or drop out"); } },
    { id: "attended",   label: "Attended", labelKey: "journeys.flow.flowTrigger.attendedEvent.label",       get sub() { return i18nT("journeys.flow.flowTrigger.attendedEvent.sub", "Fires when they check in on the day"); } },
  ],
  decisionConds: [
    "Registered within 2 days", // ui-config: stored flow value; qfValueText shows it
    "Opened the invitation", // ui-config: stored flow value; qfValueText shows it
    "Bought a ticket", // ui-config: stored flow value; qfValueText shows it
    "Checked in on the day", // ui-config: stored flow value; qfValueText shows it
    "Brought a guest", // ui-config: stored flow value; qfValueText shows it
    "Joined the waitlist", // ui-config: stored flow value; qfValueText shows it
  ],
  exitOutcomes: ["Attended", "Registered only", "Cancelled", "Declined"], // ui-config: stored flow values; qfValueText shows them
  pathLabels: ["Registered", "Declined", "Waitlisted", "Didn't open", ...FP_COMMON_LABELS], // ui-config: stored flow values; qfValueText shows them
  waitTimings: [
    { value: "after_prev",    label: "After the previous step", labelKey: "journeys.flow.waitTiming.after_prev.label" },
    { value: "before_event",  label: "Before the event", labelKey: "journeys.flow.waitTiming.before_event.label" },
    { value: "after_event",   label: "After the event", labelKey: "journeys.flow.waitTiming.after_event.label" },
  ],
  sendWhen: ["Immediately", "After previous step", "When the page is reached"], // ui-config: stored flow values; qfValueText shows them
};

/* ── The pages seam (2026-08-25) ─────────────────────────────────────
 * Page nodes were hardwired to the QUEUE page registry: QPAGE_ORDER for the
 * picker, QPAGE_META for the node header, QPageMini for the thumbnail and
 * QueuePageBuilder for the editor. A journey's pages come from a different
 * registry and are edited in the Journey Designer's own Pages view, so the
 * host now supplies a pages adapter — the same trick as the vocabulary.
 *
 * Two ways to handle "open this page": give an `Editor` (the queue's modal
 * page builder) or an `onOpenPage` (the Journey Designer navigates to its
 * Pages view). Supply exactly one.
 */
export interface QFPageMeta { label: string; labelKey?: string; sub: string; color: string; tint: string; }
export interface QFPageEntry { stepId: string; page: string; cfg: Record<string, unknown>; }
export interface QFPagesAdapter {
  /** Page types offerable in the picker, in the order they should appear. */
  order: string[];
  meta: (page: string) => QFPageMeta;
  defaultCfg: (page: string) => Record<string, unknown>;
  /** Canvas thumbnail and inspector preview — one renderer, two scales. */
  Mini: (props: { page: string; cfg: Record<string, unknown>; scale?: number; heroImage?: string }) => unknown;
  /** Copy under the inspector preview; falls back to meta().sub. */
  hint?: (page: string, cfg: Record<string, unknown>) => string;
  /** A modal editor owned by the builder (the queue's page builder). */
  Editor?: (props: {
    entries: QFPageEntry[]; startStepId: string; heroImage?: string;
    onDone: (cfgs: Record<string, Record<string, unknown>>) => void; onCancel: () => void;
  }) => unknown;
  /** Used instead of Editor when the host edits pages elsewhere. */
  onOpenPage?: (stepId: string, page: string) => void;
}

export const QF_VOCABS: Record<QFVocabId, QFVocab> = {
  queue: QF_VOCAB_QUEUE,
  appointment: QF_VOCAB_APPOINTMENT,
  event: QF_VOCAB_EVENT,
};
/** Maps a journey document's type onto its vocabulary. Anything unrecognised
 *  (a mixed-page journey, a board) falls back to the queue set. */
export const qfVocabFor = (type: string | undefined): QFVocab =>
  type === "Appointment" ? QF_VOCAB_APPOINTMENT : type === "Event" ? QF_VOCAB_EVENT : QF_VOCAB_QUEUE;
export const QF_FLOW_TRIGGER_IN = (v: QFVocab, id: string) =>
  v.flowTriggers.filter((t) => t.id === id)[0] ?? v.flowTriggers[0];

/* ── Showing stored flow values ────────────────────────────────────────
 * Conditions, outcomes, path labels, wait units and send-when values are
 * STORED in English and compared as-is, so they never change. This is only
 * what they show as. A value missing here (an author's own path name, a
 * condition from an older vocabulary) shows exactly as stored. */
const QF_VALUE_TEXT: Record<string, () => string> = {
  "Customer checks in": () => i18nT("journeys.flow.value.condition.customerChecksIn", "Customer checks in"),
  "Waited more than 10 minutes": () => i18nT("journeys.flow.value.condition.waited10", "Waited more than 10 minutes"),
  "Waited more than 20 minutes": () => i18nT("journeys.flow.value.condition.waited20", "Waited more than 20 minutes"),
  "Replied to the last message": () => i18nT("journeys.flow.value.condition.repliedLast", "Replied to the last message"),
  "No answer after 2 calls": () => i18nT("journeys.flow.value.condition.noAnswer2Calls", "No answer after 2 calls"),
  "Party size is over 4": () => i18nT("journeys.flow.value.condition.partyOver4", "Party size is over 4"),
  "Answered the booking questions": () => i18nT("journeys.flow.value.condition.answeredQuestions", "Answered the booking questions"),
  "Is a repeat visitor": () => i18nT("journeys.flow.value.condition.repeatVisitor", "Is a repeat visitor"),
  "Confirmed the appointment": () => i18nT("journeys.flow.value.condition.confirmedAppointment", "Confirmed the appointment"),
  "Opened the reminder": () => i18nT("journeys.flow.value.condition.openedReminder", "Opened the reminder"),
  "Arrived on time": () => i18nT("journeys.flow.value.condition.arrivedOnTime", "Arrived on time"),
  "Paid the deposit": () => i18nT("journeys.flow.value.condition.paidDeposit", "Paid the deposit"),
  "Is a first-time customer": () => i18nT("journeys.flow.value.condition.firstTimeCustomer", "Is a first-time customer"),
  "Rescheduled at least once": () => i18nT("journeys.flow.value.condition.rescheduledOnce", "Rescheduled at least once"),
  "Registered within 2 days": () => i18nT("journeys.flow.value.condition.registeredWithin2Days", "Registered within 2 days"),
  "Opened the invitation": () => i18nT("journeys.flow.value.condition.openedInvitation", "Opened the invitation"),
  "Bought a ticket": () => i18nT("journeys.flow.value.condition.boughtTicket", "Bought a ticket"),
  "Checked in on the day": () => i18nT("journeys.flow.value.condition.checkedInOnTheDay", "Checked in on the day"),
  "Brought a guest": () => i18nT("journeys.flow.value.condition.broughtGuest", "Brought a guest"),
  "Joined the waitlist": () => i18nT("journeys.flow.value.condition.joinedWaitlist", "Joined the waitlist"),
  "Was the customer served?": () => i18nT("journeys.flow.value.condition.wasServed", "Was the customer served?"),
  "Served": () => i18nT("journeys.flow.value.outcome.served", "Served"),
  "Left the queue": () => i18nT("journeys.flow.value.outcome.leftQueue", "Left the queue"),
  "No-show": () => i18nT("journeys.flow.value.outcome.noShow", "No-show"),
  "Transferred out": () => i18nT("journeys.flow.value.outcome.transferredOut", "Transferred out"),
  "Removed by staff": () => i18nT("journeys.flow.value.outcome.removedByStaff", "Removed by staff"),
  "Attended": () => i18nT("journeys.flow.value.outcome.attended", "Attended"),
  "Cancelled": () => i18nT("journeys.flow.value.outcome.cancelled", "Cancelled"),
  "Rescheduled out": () => i18nT("journeys.flow.value.outcome.rescheduledOut", "Rescheduled out"),
  "Registered only": () => i18nT("journeys.flow.value.outcome.registeredOnly", "Registered only"),
  "Declined": () => i18nT("journeys.flow.value.outcome.declined", "Declined"),
  "Minutes": () => i18nT("journeys.flow.value.unit.minutes", "Minutes"),
  "Hours": () => i18nT("journeys.flow.value.unit.hours", "Hours"),
  "Days": () => i18nT("journeys.flow.value.unit.days", "Days"),
  "Immediately": () => i18nT("journeys.flow.value.sendWhen.immediately", "Immediately"),
  "After previous step": () => i18nT("journeys.flow.value.sendWhen.afterPrevious", "After previous step"),
  "When the stage is reached": () => i18nT("journeys.flow.value.sendWhen.stageReached", "When the stage is reached"),
  "When the page is reached": () => i18nT("journeys.flow.value.sendWhen.pageReached", "When the page is reached"),
  "Checked in": () => i18nT("journeys.flow.value.path.checkedIn", "Checked in"),
  "Didn't check in": () => i18nT("journeys.flow.value.path.didntCheckIn", "Didn't check in"),
  "Still waiting": () => i18nT("journeys.flow.value.path.stillWaiting", "Still waiting"),
  "Confirmed": () => i18nT("journeys.flow.value.path.confirmed", "Confirmed"),
  "Not confirmed": () => i18nT("journeys.flow.value.path.notConfirmed", "Not confirmed"),
  "Rescheduled": () => i18nT("journeys.flow.value.path.rescheduled", "Rescheduled"),
  "Registered": () => i18nT("journeys.flow.value.path.registered", "Registered"),
  "Waitlisted": () => i18nT("journeys.flow.value.path.waitlisted", "Waitlisted"),
  "Didn't open": () => i18nT("journeys.flow.value.path.didntOpen", "Didn't open"),
  "Yes": () => i18nT("journeys.flow.value.path.yes", "Yes"),
  "No": () => i18nT("journeys.flow.value.path.no", "No"),
  "Otherwise": () => i18nT("journeys.flow.value.path.otherwise", "Otherwise"),
  "No answer in time": () => i18nT("journeys.flow.value.path.noAnswerInTime", "No answer in time"),
  "Custom…": () => i18nT("journeys.flow.value.path.custom", "Custom…"),
};
/** What a stored flow value shows as, in the viewer's language. */
export function qfValueText(value: string): string {
  const text = QF_VALUE_TEXT[value];
  return text ? text() : value;
}

let _qfSeq = 0;
const qfId = (p: string) => `${p}_${Date.now().toString(36)}${(_qfSeq++).toString(36)}${Math.random().toString(36).slice(2, 4)}`;

export interface QStepStage   { id: string; kind: "stage";    stage: QFStageId; }
export interface QStepTrigger { id: string; kind: "qtrigger"; event: string; }
export interface QStepPage    { id: string; kind: "page";     page: string; cfg: Record<string, unknown>; }
/** `hint` — what a DERIVED journey suggests this step should send, before a
 *  real message is attached. Seeded journeys used to carry that as a prose
 *  title on the document's own step shape; now that the document stores this
 *  tree directly, the hint travels with the step and the node shows it instead
 *  of a bare "Pick a message". `promo` marks a win-back send — the designer's
 *  attachment strip reads it. */
export interface QStepComms   { id: string; kind: "comms";    messageId: string | null; sendWhen: string; landingId: string; hint?: string; promo?: boolean; }
export interface QStepWait    { id: string; kind: "wait";     amount: number; unit: string; timing: string; }
/** `paths` is ordered: first is the met path, last is the fallback. Two entries
 *  render exactly as YES / NO always did; a third is added explicitly.
 *  evalAmount/evalUnit — how long to wait for the condition before taking the
 *  fallback path. */
export interface QStepDecision{ id: string; kind: "decision"; condition: string; paths: FlowPath<QFStep>[]; evalAmount?: number; evalUnit?: string; }
export interface QStepSurvey  { id: string; kind: "survey";   surveyId: string | null; sendWhen: string; hint?: string; }
export interface QStepExit    { id: string; kind: "exit";     outcome: string; note: string; }
export type QFStep = QStepStage | QStepTrigger | QStepPage | QStepComms | QStepWait | QStepDecision | QStepSurvey | QStepExit;

export interface QFFlow { trigger: { event: string }; steps: QFStep[]; }

export const qfMkStage    = (stage: QFStageId): QStepStage => ({ id: qfId("st"), kind: "stage", stage });
export const qfMkTrigger  = (event?: string, v: QFVocab = QF_VOCAB_QUEUE): QStepTrigger => ({ id: qfId("tg"), kind: "qtrigger", event: event ?? v.flowTriggers[0].id });
export const qfMkPage     = (page: string, cfg?: Record<string, unknown>): QStepPage => ({ id: qfId("pg"), kind: "page", page, cfg: cfg ?? qpDefaultCfg(page as QPageId) });
export const qfMkComms    = (messageId: string | null = null, hint?: string): QStepComms => ({ id: qfId("c"), kind: "comms", messageId, sendWhen: "Immediately", landingId: "", /* ui-config: stored flow value */ ...(hint ? { hint } : {}) });
export const qfMkWait     = (v: QFVocab = QF_VOCAB_QUEUE): QStepWait => ({ id: qfId("w"), kind: "wait", amount: 5, unit: "Minutes", /* ui-config: stored flow value */ timing: v.waitTimings[0].value });
export const qfMkDecision = (v: QFVocab = QF_VOCAB_QUEUE): QStepDecision => ({ id: qfId("d"), kind: "decision", condition: v.decisionConds[0], paths: fpDefaultPaths<QFStep>() });
export const qfMkSurvey   = (surveyId: string | null = null, hint?: string): QStepSurvey => ({ id: qfId("sv"), kind: "survey", surveyId, sendWhen: "Immediately", /* ui-config: stored flow value */ ...(hint ? { hint } : {}) });
export const qfMkExit     = (outcome?: string, v: QFVocab = QF_VOCAB_QUEUE): QStepExit => ({ id: qfId("x"), kind: "exit", outcome: outcome ?? v.exitOutcomes[0], note: "" });

/** Default flow — the full page-based customer journey:
 *  Services → Wait Time → Contact Details → join confirmation → Waiting in
 *  queue → turn-coming-up → it's-your-turn → thank-you → How did we do? */
export function qfDefaultFlow(): QFFlow {
  return {
    trigger: { event: "screen_services" },
    steps: [
      qfMkPage("services"),
      qfMkPage("wait"),
      qfMkPage("contact"),
      qfMkComms(),                 // queue-join confirmation
      qfMkPage("waiting"),
      qfMkPage("noservice"),      // required — shown when the customer leaves unserved
      qfMkComms(),                 // your turn is coming up
      qfMkComms(),                 // it's your turn
      qfMkComms(),                 // thank you for your business
      qfMkPage("csat"),
    ],
  };
}

/** Per-TYPE default flows — operator-built REFERENCE implementations, captured
 *  verbatim 2026-07-21:
 *  · quick    = the "Quick Queue test" queue's flow (stock hero image omitted —
 *               the queue hero / gradient tops the pages instead).
 *  · standard = the "Me Nombre es Queue" queue's flow. */
export function qfTypeDefaultFlow(typeId: string): QFFlow {
  if (typeId === "quick") {
    return {
      trigger: { event: "screen_services" },
      steps: [
        qfMkPage("services"),
        qfMkPage("contact"),
        qfMkTrigger("joined"),
        qfMkComms("msg_q_join"),
        qfMkPage("waiting", { ...qpDefaultCfg("waiting"), allowMessaging: false }),
        qfMkComms("msg_q_turn"),
        qfMkTrigger("served"),
        qfMkExit("Served"), // ui-config: stored flow value
      ],
    };
  }
  const dec = qfMkDecision(); // "Customer checks in"
  dec.paths = [
    { key: "yes", label: "Yes", steps: [ // ui-config: stored flow value
      qfMkTrigger("served"),
      qfMkPage("csat"),
      { ...qfMkWait(), amount: 10, timing: "after_service" },
      { ...qfMkComms("msg_q_thanks"), sendWhen: "After previous step" }, // ui-config: stored flow value
      qfMkExit("Served"), // ui-config: stored flow value
    ] },
    { key: "no", label: "No", steps: [ // ui-config: stored flow value
      { ...qfMkWait(), amount: 2, timing: "after_called" },
      qfMkComms("msg_q_turn"),
      { ...qfMkWait(), amount: 2, timing: "after_prev" },
      qfMkPage("noservice"),
      qfMkComms("msg_q_missed"),
      qfMkTrigger("left"),
      qfMkExit("No-show"), // ui-config: stored flow value
    ] },
  ];
  return {
    trigger: { event: "screen_services" },
    steps: [
      qfMkPage("services"),
      qfMkPage("wait"),
      qfMkPage("contact"),
      qfMkTrigger("joined"),
      qfMkComms("msg_q_join"),
      qfMkPage("waiting"),
      qfMkComms("msg_q_turn"),
      dec,
    ],
  };
}

/** Migrate a stored flow: old lifecycle-stage spines become page spines, old
 *  trigger ids map to their screen equivalents, and a decision stored as the old
 *  `{ yes, no }` pair gains its ordered `paths` list. Idempotent. (The
 *  Left-without-service page is seeded by default but DELETABLE — operator
 *  2026-07-21; when absent, the public surface falls back to the default
 *  left-without-service card.) */
export function qfMigrate(flow: QFFlow): QFFlow {
  const trigger = { event: QF_TRIGGER(flow.trigger?.event ?? "screen_services").id };
  const stageToPage: Partial<Record<QFStageId, QPageId>> = { waiting: "waiting", done: "csat" };
  const walk = (arr: QFStep[]): QFStep[] => (arr ?? []).flatMap((s): QFStep[] => {
    if (s.kind === "stage") { const p = stageToPage[s.stage]; return p ? [{ id: s.id, kind: "page", page: p, cfg: qpDefaultCfg(p) }] : []; }
    /* Same cast as `qfMkPage` (:244): a stored `page` is a plain string on the
       step, and the page registry is keyed by `QPageId`. Unknown keys fall back
       inside `qpDefaultCfg`, so the cast narrows a lookup, not a guarantee. */
    if (s.kind === "page" && !s.cfg) return [{ ...s, cfg: qpDefaultCfg(s.page as QPageId) }];
    if (s.kind === "decision") {
      const paths = fpMigrate<QFStep>(s).map((p) => ({ ...p, steps: walk(p.steps) }));
      const { yes: _y, no: _n, ...rest } = s as unknown as Record<string, unknown>;
      return [{ ...(rest as unknown as QStepDecision), paths }];
    }
    return [s];
  });
  return { trigger, steps: walk(flow?.steps ?? []) };
}

/* ── Tree helpers (identical semantics to fb-model) ─────────────────── */
export function qfFindStep(steps: QFStep[], id: string): QFStep | null {
  for (const s of steps) {
    if (s.id === id) return s;
    if (s.kind === "decision") {
      for (const p of (s.paths ?? [])) { const r = qfFindStep(p.steps ?? [], id); if (r) return r; }
    }
  }
  return null;
}
export function qfEdit(steps: QFStep[], id: string, fn: (s: QFStep) => QFStep): QFStep[] {
  return steps.map((s) => {
    if (s.id === id) return fn(s);
    if (s.kind === "decision") return { ...s, paths: fpMapSteps(s.paths, (list) => qfEdit(list, id, fn)) };
    return s;
  });
}
export function qfRemove(steps: QFStep[], id: string): QFStep[] {
  return steps.filter((s) => s.id !== id).map((s) =>
    s.kind === "decision" ? { ...s, paths: fpMapSteps(s.paths, (list) => qfRemove(list, id)) } : s);
}
/** `pathKey` names which branch of `parentId` to insert into — null for the
 *  trunk. Was `branch: "yes" | "no"`. */
export interface QInsertDesc { parentId: string | null; pathKey: string | null; index: number; }
export function qfInsert(steps: QFStep[], desc: QInsertDesc, step: QFStep): QFStep[] {
  if (!desc.parentId) { const n = steps.slice(); n.splice(desc.index, 0, step); return n; }
  return steps.map((s) => {
    if (s.kind !== "decision") return s;
    if (s.id === desc.parentId) {
      return { ...s, paths: (s.paths ?? []).map((p) => {
        if (p.key !== desc.pathKey) return p;
        const arr = (p.steps ?? []).slice();
        arr.splice(desc.index, 0, step);
        return { ...p, steps: arr };
      }) };
    }
    return { ...s, paths: fpMapSteps(s.paths, (list) => qfInsert(list, desc, step)) };
  });
}

/**
 * Move an existing step to another position — including onto a different path.
 *
 * Remove-then-insert rather than a bespoke walk: `qfRemove` and `qfInsert`
 * already handle the nesting, and reusing them means a move cannot disagree with
 * a delete or an add about what the tree looks like.
 *
 * TWO GUARDS, both of which produce a broken flow rather than a wrong one:
 *
 *  · A decision cannot move INTO ITS OWN subtree. The step carries its paths
 *    with it, so inserting it inside one of them detaches that whole branch from
 *    the tree — it is still in the document, reachable from nothing. Refused.
 *
 *  · The index shifts when the removal happens BEFORE the target in the same
 *    list. Dropping step 1 of the trunk onto slot 3 means index 3 of a list that
 *    no longer contains it, which lands one place early. Adjusted here rather
 *    than at the call site, because every caller would need the same correction.
 */
export function qfMove(steps: QFStep[], id: string, desc: QInsertDesc): QFStep[] {
  const step = qfFindStep(steps, id);
  if (!step) return steps;
  if (desc.parentId === id) return steps;
  if (step.kind === "decision" && qfFindStep((step.paths ?? []).flatMap((p) => p.steps ?? []), desc.parentId ?? "")) return steps;
  /* Where is it now? Only matters when it is being dropped into the SAME list. */
  const sameList = (list: QFStep[] | undefined): number => (list ?? []).findIndex((s) => s.id === id);
  const from = desc.parentId === null
    ? sameList(steps)
    : (() => {
        const parent = qfFindStep(steps, desc.parentId);
        if (!parent || parent.kind !== "decision") return -1;
        return sameList((parent.paths ?? []).find((p) => p.key === desc.pathKey)?.steps);
      })();
  const index = from >= 0 && from < desc.index ? desc.index - 1 : desc.index;
  if (from >= 0 && (index === from)) return steps;
  return qfInsert(qfRemove(steps, id), { ...desc, index }, step);
}

/** Is this step a legal drop target's ancestor? Used by the canvas to refuse a
 *  decision dropped inside itself before the drag finishes, so the cursor says
 *  no rather than the drop silently doing nothing. */
export function qfCanMove(steps: QFStep[], id: string, parentId: string | null): boolean {
  if (parentId === null) return true;
  if (parentId === id) return false;
  const step = qfFindStep(steps, id);
  if (!step || step.kind !== "decision") return true;
  return !qfFindStep((step.paths ?? []).flatMap((p) => p.steps ?? []), parentId);
}

/** Every step in the tree, in document order — the walk almost every caller
 *  outside the builder actually wants (counts, page usage, promo lookups). */
export function qfFlatten(steps: QFStep[]): QFStep[] {
  const out: QFStep[] = [];
  const walk = (arr: QFStep[]) => (arr ?? []).forEach((s) => {
    out.push(s);
    if (s.kind === "decision") (s.paths ?? []).forEach((p) => walk(p.steps ?? []));
  });
  walk(steps);
  return out;
}

/** Map every step in the tree, preserving structure. */
export function qfMapAll(steps: QFStep[], fn: (s: QFStep) => QFStep | null): QFStep[] {
  const walk = (arr: QFStep[]): QFStep[] => (arr ?? []).flatMap((s): QFStep[] => {
    if (s.kind === "decision") {
      const next = fn({ ...s, paths: fpMapSteps(s.paths, walk) });
      return next ? [next] : [];
    }
    const next = fn(s);
    return next ? [next] : [];
  });
  return walk(steps);
}

/** Human label for a wait. Timing ids come from the vocabulary, so an
 *  appointment wait reads "24 hours before the appointment" and a queue wait
 *  reads "2 minutes after the call" from the same code path. */
export function qfWaitLabel(s: QStepWait, v: QFVocab = QF_VOCAB_QUEUE): string {
  const qty = i18nT("journeys.flow.wait.quantity", "{unit, select, Minutes {{amount, plural, one {# minute} other {# minutes}}} Hours {{amount, plural, one {# hour} other {# hours}}} Days {{amount, plural, one {# day} other {# days}}} other {{amount} {unitText}}}", { unit: String(s.unit || ""), amount: s.amount, unitText: String(s.unit || "").toLowerCase() });
  if (s.timing === "after_prev") return i18nT("journeys.flow.wait.plain", "Wait {quantity}", { quantity: qty });
  const hit = v.waitTimings.filter((t) => t.value === s.timing)[0];
  if (!hit) return i18nT("journeys.flow.wait.plain", "Wait {quantity}", { quantity: qty });
  return i18nT("journeys.flow.wait.timed", "{timing, select, after_called {{quantity} after the customer is called} after_service {{quantity} after the service ends} before_appt {{quantity} before the appointment} after_appt {{quantity} after the appointment} before_event {{quantity} before the event} after_event {{quantity} after the event} other {{quantity} {timingText}}}", { timing: s.timing, quantity: qty, timingText: hit.label.replace(/^After /, "after ").replace(/^Before /, "before ") });
}
